
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { formatDistanceToNow } from "date-fns";
import { Target, Calendar, Coins, Edit, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";

// Define the type for our goal props
export interface SavingsGoal {
  id: string;
  name: string;
  target: number;
  currentAmount: number;
  category: "emergency" | "retirement" | "house" | "car" | "vacation" | "education" | "other";
  description?: string;
  targetDate?: Date;
  contributionAmount?: number;
  contributionFrequency?: "weekly" | "biweekly" | "monthly";
  createdAt: Date;
}

interface GoalCardProps {
  goal: SavingsGoal;
  onEdit: (goal: SavingsGoal) => void;
  onDelete: (id: string) => void;
  onContribute: (id: string) => void;
}

// Helper function to get category icon
const getCategoryIcon = (category: SavingsGoal["category"]) => {
  switch (category) {
    case "emergency":
      return Target;
    case "retirement":
      return Calendar;
    default:
      return Coins;
  }
};

// Helper function to get category color
const getCategoryColor = (category: SavingsGoal["category"]) => {
  switch (category) {
    case "emergency":
      return "bg-red-100 text-red-600";
    case "retirement":
      return "bg-blue-100 text-blue-600";
    case "house":
      return "bg-green-100 text-green-600";
    case "car":
      return "bg-purple-100 text-purple-600";
    case "vacation":
      return "bg-yellow-100 text-yellow-600";
    case "education":
      return "bg-pink-100 text-pink-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

export const GoalCard = ({ goal, onEdit, onDelete, onContribute }: GoalCardProps) => {
  // Calculate progress percentage
  const progressPercentage = Math.min(
    Math.round((goal.currentAmount / goal.target) * 100),
    100
  );
  
  // Format category for display
  const formattedCategory = goal.category.charAt(0).toUpperCase() + goal.category.slice(1);
  
  // Get appropriate icon
  const CategoryIcon = getCategoryIcon(goal.category);
  
  // Get category color class
  const categoryColorClass = getCategoryColor(goal.category);

  return (
    <Card className="h-full hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        {/* Category badge */}
        <div className="flex justify-between items-center mb-1">
          <div className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${categoryColorClass}`}>
            <CategoryIcon className="w-3 h-3" />
            {formattedCategory}
          </div>
          <span className="text-xs text-gray-500">
            Created {formatDistanceToNow(goal.createdAt, { addSuffix: true })}
          </span>
        </div>
        
        <CardTitle className="text-xl">{goal.name}</CardTitle>
        <CardDescription className="line-clamp-2 h-10">
          {goal.description || "No description provided"}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium">${goal.currentAmount.toFixed(2)}</span>
            <span className="text-muted-foreground">of ${goal.target.toFixed(2)}</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{progressPercentage}% complete</span>
            <span>${(goal.target - goal.currentAmount).toFixed(2)} remaining</span>
          </div>
        </div>
        
        {/* Target date if available */}
        {goal.targetDate && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>
              Target: {goal.targetDate.toLocaleDateString()}
              {new Date() > goal.targetDate ? 
                " (Overdue)" : 
                ` (${formatDistanceToNow(goal.targetDate, { addSuffix: true })})`}
            </span>
          </div>
        )}
        
        {/* Contribution plan if available */}
        {goal.contributionAmount && goal.contributionFrequency && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Coins className="w-4 h-4" />
            <span>
              Contributing ${goal.contributionAmount.toFixed(2)} {goal.contributionFrequency}
            </span>
          </div>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between pt-2">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(goal)}>
            <Edit className="w-4 h-4 mr-1" /> Edit
          </Button>
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" 
                  onClick={() => onDelete(goal.id)}>
            <Trash className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
        <Button size="sm" onClick={() => onContribute(goal.id)}>
          <Coins className="w-4 h-4 mr-1" /> Contribute
        </Button>
      </CardFooter>
    </Card>
  );
};
